import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { escape, filter, flow, groupBy, map, partialRight, reduce } from 'lodash-es';
import pluralize from 'pluralize';

import { getAllBlocks, getBlockDisplayName, isClauseBlock } from '../blocks';
import type { BlockMentionValues } from '../blocks/get-block-mention-values';

import type { ClauseType } from './constants';
import { CLAUSE_TYPES } from './constants';
import type { ClauseListKey, GroupedClauses } from './types';

export const keyMap: Record<ClauseType, ClauseListKey> = {
  Condition: 'list_of_condition',
  Endorsement: 'list_of_endorsement',
  Exclusion: 'list_of_exclusion',
  Wording: 'list_of_wording',
  'Express Warranties': 'list_of_express_warranties',
  'Standard Warranties': 'list_of_standard_warranties',
};

const CLAUSE_NAME_FALLBACK = 'Untitled clause';

export const getClauseListEntryContent = (block: ResourceBlock) => {
  const name = getBlockDisplayName(block) || CLAUSE_NAME_FALLBACK;

  return block.clause_reference ? `${block.clause_reference} - ${name}` : name;
};

export const getClauseTypeForClauseListKey = (value: ClauseListKey): ClauseType | undefined => {
  const keys = Object.keys(keyMap) as ClauseType[];
  return keys.find((key) => keyMap[key] === value);
};

export const getClauseList = (
  blocks: ResourceBlock[] = [],
  type: ClauseType,
  fallback = `No ${pluralize(type.toLocaleLowerCase())} specified`,
): string => {
  if (blocks.length === 0) return fallback;

  return blocks
    .map((block) => `<span class="mention-list-item">${escape(getClauseListEntryContent(block))}, as attached.</span>`)
    .join('');
};

export const getClausesAsMentions = (clauses: GroupedClauses): BlockMentionValues => {
  const lists: Record<ClauseListKey, string> = flow(
    partialRight(map, (type: ClauseType) => ({
      [keyMap[type]]: getClauseList(clauses[type], type),
    })),
    partialRight(reduce, (acc: any, next: any) => ({ ...acc, ...next }), {}),
  )(CLAUSE_TYPES);

  return lists;
};

type EntityWithSections = {
  sections: Array<{
    id: string;
    layout?: string[];
    blocks: { id: string }[];
  }>;
};

export const getGroupedClauses = (entity?: EntityWithSections): GroupedClauses => {
  if (!entity?.sections) {
    return {} as GroupedClauses;
  }

  return flow(
    partialRight(getAllBlocks),
    partialRight(
      filter,
      (block: Pick<ResourceBlock, 'deleted_at' | 'type'>) => !block.deleted_at && isClauseBlock(block),
    ),
    partialRight(groupBy, 'clause_type'),
  )(entity);
};
